export type RawParams =
  | Record<string, number | string | null | undefined>
  | null
  | undefined;

export interface ResolvedMetrics {
  battery: number | null;
  satellites: number | null;
  ignition: boolean | null;
  sos: number | null;
  odometer: number | null;
  engineCutoff: boolean | null;
  hitch: boolean | null;
  idle: boolean | null;
}

const SOS_ACTIVE_CODE = 5;

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export interface ParamSourceHit {
  key: string;
  raw: number | string;
  value: number;
}

const toSourceList = (source: string | string[]): string[] =>
  Array.isArray(source) ? source : [source];

export const findParamSource = (
  params: RawParams,
  source: string | string[],
): ParamSourceHit | null => {
  if (!params) return null;
  const keys = toSourceList(source);
  const hit = (key: string): ParamSourceHit | null => {
    const value = num(params[key]);
    if (value == null) return null;
    return { key, raw: params[key] as number | string, value };
  };

  const direct = keys.map(hit).find((entry) => entry != null);
  if (direct) return direct;

  const wanted = keys.map((k) => k.toLowerCase());
  return (
    Object.keys(params)
      .filter((paramKey) => wanted.includes(paramKey.toLowerCase()))
      .map(hit)
      .find((entry) => entry != null) ?? null
  );
};

const pick = (params: RawParams, source: string | string[]): number | null =>
  findParamSource(params, source)?.value ?? null;

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, Math.round(value)));

const liIonFromVolts = (volts: number): number =>
  clampPercent(((volts - 3.3) / (4.2 - 3.3)) * 100);

const liIonFromAnyScale = (raw: number): number | null => {
  if (raw >= 2.5 && raw <= 4.5) return liIonFromVolts(raw);
  if (raw >= 2500 && raw <= 4500) return liIonFromVolts(raw / 1000);
  return null;
};

export const BATTERY_TYPES: Record<string, (raw: number) => number | null> = {
  percent: (raw) => clampPercent(raw),
  liion: (raw) => liIonFromAnyScale(raw),
  voltage: (raw) => liIonFromAnyScale(raw),
  millipercent: (raw) => clampPercent(raw / 1000),
};

export const ODOMETER_TYPES: Record<string, (raw: number) => number | null> = {
  km: (raw) => Math.round(raw),
  meters: (raw) => Math.round(raw / 1000),
  centimeters: (raw) => Math.round(raw / 100000),
  millimeters: (raw) => Math.round(raw / 1000000),
};

export const TRANSFORM_RANGES: Record<string, string> = {
  liion: "2.5–4.5 V · 2500–4500 mV",
  voltage: "2.5–4.5 V · 2500–4500 mV",
};

export const FLAG_TYPES: Record<string, (raw: number) => boolean> = {
  flag: (raw) => raw === 1,
  flagInverted: (raw) => raw === 0,
  engine: (raw) => raw === 1 || raw === 2 || raw === 3,
};

type BatteryType = keyof typeof BATTERY_TYPES;
type OdometerType = keyof typeof ODOMETER_TYPES;
type FlagType = keyof typeof FLAG_TYPES;

const anyFlag = (
  params: RawParams,
  source: string | string[],
  type: FlagType,
): boolean | null => {
  const flags = toSourceList(source)
    .map((key) => pick(params, key))
    .filter((raw): raw is number => raw != null)
    .map((raw) => FLAG_TYPES[type](raw));
  return flags.length === 0 ? null : flags.some(Boolean);
};

interface ModelConfig {
  battery?: { source: string | string[]; type: BatteryType };
  odometer?: { source: string | string[]; type: OdometerType };
  ignition?: { source: string | string[]; type: FlagType };
  satellites?: { source: string | string[] };
  sos?: { source: string | string[]; activeCode?: number };
  engineCutoff?: { source: string | string[]; type: FlagType };
  hitch?: { source: string | string[]; type: FlagType };
  idle?: { source: string | string[]; type: FlagType };
}

const ECO_TRACE_CONFIG: ModelConfig = {
  battery: { source: "battery_voltage", type: "liion" },
  odometer: { source: "virtual_odometer", type: "meters" },
  ignition: { source: "custom_ignition", type: "flag" },
  satellites: { source: ["satellites", "gps_sat"] },
  sos: { source: "ain1", activeCode: SOS_ACTIVE_CODE },
};

const HITCH_CONFIG: ModelConfig = {
  hitch: { source: "hook_sensor", type: "flag" },
};

export const HITCH_MODELS = ["tlw2", "gv75"] as const;

export const ENGINE_CUTOFF_BRANDS = ["ruptela"] as const;

const MODEL_CONFIGS: Record<string, ModelConfig> = {
  eco5: ECO_TRACE_CONFIG,
  "trace v1": ECO_TRACE_CONFIG,
  "trace5 v1": ECO_TRACE_CONFIG,
  "trace v2": ECO_TRACE_CONFIG,
  "trace5 v2": ECO_TRACE_CONFIG,
  tlw2: HITCH_CONFIG,
  gv75: HITCH_CONFIG,
};

const BRAND_CONFIGS: Record<string, ModelConfig> = {
  ruptela: {
    engineCutoff: { source: "dout1_status", type: "flagInverted" },
  },
};

const RUPTELA_IDLE_MODELS = ["eco5", "trace5"] as const;

const RUPTELA_IDLE_CONFIG: ModelConfig = {
  idle: { source: ["ignition(din4)", "eco_idling_event"], type: "flag" },
};

const BATTERY_VOLTAGE_SOURCES = ["battery_voltage", "bat_voltage"];
const BATTERY_GENERIC_SOURCES = ["battery", "battery_level", "bat", "internal_battery"];
const SATELLITES_SOURCES = ["satellites", "gps_sat", "sat", "sats", "gps_satellites"];
const IGNITION_FLAG_SOURCES = ["custom_ignition", "ignition", "ignition_status"];
const IGNITION_ENGINE_SOURCES = ["engine_status"];
const ODOMETER_KM_SOURCES = ["odo_km", "odometer_km"];
const ODOMETER_METERS_SOURCES = ["virtual_odometer", "total_odometer", "odometer"];

const resolveBatteryGeneral = (params: RawParams): number | null => {
  const volts = pick(params, BATTERY_VOLTAGE_SOURCES);
  if (volts != null) {
    const fromVolts = liIonFromAnyScale(volts);
    if (fromVolts != null) return fromVolts;
  }
  const generic = pick(params, BATTERY_GENERIC_SOURCES);
  if (generic == null) return null;
  if (generic > 100) {
    const fromVolts = liIonFromAnyScale(generic);
    if (fromVolts != null) return fromVolts;
  }
  return clampPercent(generic);
};

const resolveSatellitesGeneral = (params: RawParams): number | null =>
  pick(params, SATELLITES_SOURCES);

const resolveIgnitionGeneral = (params: RawParams): boolean | null => {
  const flag = pick(params, IGNITION_FLAG_SOURCES);
  if (flag != null) return FLAG_TYPES.flag(flag);
  const engine = pick(params, IGNITION_ENGINE_SOURCES);
  if (engine != null) return FLAG_TYPES.engine(engine);
  return null;
};

const resolveOdometerGeneral = (params: RawParams): number | null => {
  const km = pick(params, ODOMETER_KM_SOURCES);
  if (km != null) return Math.round(km);
  const meters = pick(params, ODOMETER_METERS_SOURCES);
  return meters == null ? null : ODOMETER_TYPES.meters(meters);
};

const generalMetrics = (params: RawParams): ResolvedMetrics => ({
  battery: resolveBatteryGeneral(params),
  satellites: resolveSatellitesGeneral(params),
  ignition: resolveIgnitionGeneral(params),
  sos: null,
  odometer: resolveOdometerGeneral(params),
  engineCutoff: null,
  hitch: null,
  idle: null,
});

const applyConfig = (params: RawParams, cfg: ModelConfig): Partial<ResolvedMetrics> => {
  const out: Partial<ResolvedMetrics> = {};

  if (cfg.battery) {
    const raw = pick(params, cfg.battery.source);
    out.battery = raw == null ? null : BATTERY_TYPES[cfg.battery.type](raw);
  }
  if (cfg.odometer) {
    const raw = pick(params, cfg.odometer.source);
    out.odometer = raw == null ? null : ODOMETER_TYPES[cfg.odometer.type](raw);
  }
  if (cfg.ignition) {
    const raw = pick(params, cfg.ignition.source);
    out.ignition = raw == null ? null : FLAG_TYPES[cfg.ignition.type](raw);
  }
  if (cfg.satellites) {
    out.satellites = pick(params, cfg.satellites.source);
  }
  if (cfg.sos) {
    const raw = pick(params, cfg.sos.source);
    out.sos =
      raw == null
        ? null
        : raw === 1
          ? cfg.sos.activeCode ?? SOS_ACTIVE_CODE
          : raw > 1
            ? raw
            : null;
  }
  if (cfg.engineCutoff) {
    const raw = pick(params, cfg.engineCutoff.source);
    out.engineCutoff = raw == null ? null : FLAG_TYPES[cfg.engineCutoff.type](raw);
  }
  if (cfg.hitch) {
    const raw = pick(params, cfg.hitch.source);
    out.hitch = raw == null ? null : FLAG_TYPES[cfg.hitch.type](raw);
  }
  if (cfg.idle) {
    out.idle = anyFlag(params, cfg.idle.source, cfg.idle.type);
  }

  return out;
};

const normalizeStr = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

export const matchesModel = (
  brandModel: string | null | undefined,
  tokens: readonly string[],
): boolean => {
  const model = normalizeStr(brandModel);
  if (!model) return false;
  return tokens.some((token) => model.includes(token));
};

const findModelEntry = (
  registry: Record<string, ModelConfig>,
  model: string,
): { token: string; config: ModelConfig } | undefined => {
  if (!model) return undefined;
  if (registry[model]) return { token: model, config: registry[model] };
  const token = Object.keys(registry).find((key) => model.includes(key));
  return token ? { token, config: registry[token] } : undefined;
};

const findModelConfig = (
  registry: Record<string, ModelConfig>,
  model: string,
): ModelConfig | undefined => findModelEntry(registry, model)?.config;

export type MetricKey = keyof ResolvedMetrics;

export interface MetricSourceCandidate {
  sources: string[];
  transform: string;
  scope: string;
}

export type MetricSourceMap = Record<MetricKey, MetricSourceCandidate[]>;

const GENERAL_SOURCE_MAP: MetricSourceMap = {
  battery: [
    { sources: BATTERY_VOLTAGE_SOURCES, transform: "liion", scope: "general" },
    { sources: BATTERY_GENERIC_SOURCES, transform: "percent", scope: "general" },
  ],
  satellites: [{ sources: SATELLITES_SOURCES, transform: "raw", scope: "general" }],
  ignition: [
    { sources: IGNITION_FLAG_SOURCES, transform: "flag", scope: "general" },
    { sources: IGNITION_ENGINE_SOURCES, transform: "engine", scope: "general" },
  ],
  odometer: [
    { sources: ODOMETER_KM_SOURCES, transform: "km", scope: "general" },
    { sources: ODOMETER_METERS_SOURCES, transform: "meters", scope: "general" },
  ],
  sos: [],
  engineCutoff: [],
  hitch: [],
  idle: [],
};

const configCandidate = (
  entry: { source: string | string[]; type?: string } | undefined,
  fallbackTransform: string,
  scope: string,
): MetricSourceCandidate[] =>
  entry
    ? [
        {
          sources: toSourceList(entry.source),
          transform: entry.type ?? fallbackTransform,
          scope,
        },
      ]
    : [];

const configSourceMap = (config: ModelConfig, scope: string): MetricSourceMap => ({
  battery: configCandidate(config.battery, "percent", scope),
  satellites: configCandidate(config.satellites, "raw", scope),
  ignition: configCandidate(config.ignition, "flag", scope),
  odometer: configCandidate(config.odometer, "km", scope),
  sos: configCandidate(config.sos, "code", scope),
  engineCutoff: configCandidate(config.engineCutoff, "flag", scope),
  hitch: configCandidate(config.hitch, "flag", scope),
  idle: configCandidate(config.idle, "flag", scope),
});

const METRIC_KEYS = Object.keys(GENERAL_SOURCE_MAP) as MetricKey[];

export const describeMetricSources = (
  brandModel?: string | null,
  brandName?: string | null,
): MetricSourceMap => {
  const manufacturer = normalizeStr(brandName);
  const model = normalizeStr(brandModel);
  const modelEntry = findModelEntry(MODEL_CONFIGS, model);
  const brandConfig = BRAND_CONFIGS[manufacturer];
  const hasIdleConfig =
    manufacturer === "ruptela" && matchesModel(model, RUPTELA_IDLE_MODELS);

  const maps = [
    hasIdleConfig ? configSourceMap(RUPTELA_IDLE_CONFIG, `${manufacturer}:idle`) : null,
    modelEntry ? configSourceMap(modelEntry.config, `modelo:${modelEntry.token}`) : null,
    brandConfig ? configSourceMap(brandConfig, `marca:${manufacturer}`) : null,
    GENERAL_SOURCE_MAP,
  ].filter((map): map is MetricSourceMap => map != null);

  return METRIC_KEYS.reduce((acc, metric) => {
    acc[metric] = maps.flatMap((map) => map[metric]);
    return acc;
  }, {} as MetricSourceMap);
};

export const resolveMetricsFromParams = (
  params: RawParams,
  brandModel?: string | null,
  brandName?: string | null,
): ResolvedMetrics => {
  const result = generalMetrics(params);

  const manufacturer = normalizeStr(brandName);
  const model = normalizeStr(brandModel);

  const configs = [
    BRAND_CONFIGS[manufacturer],
    findModelConfig(MODEL_CONFIGS, model),
    manufacturer === "ruptela" && matchesModel(model, RUPTELA_IDLE_MODELS)
      ? RUPTELA_IDLE_CONFIG
      : undefined,
  ];

  for (const cfg of configs) {
    if (!cfg) continue;
    const extra = applyConfig(params, cfg);
    for (const key of Object.keys(extra) as (keyof ResolvedMetrics)[]) {
      const value = extra[key];
      if (value != null) (result as unknown as Record<string, unknown>)[key] = value;
    }
  }

  return result;
};
